import type { ReactNode } from 'react';

import { makeRef } from './makeRef.tsx';
import type { DomainCatalog, DomainConfig, DomainEntry, DomainKind, DomainRef } from './types.ts';
import { validateRendered } from './validation.ts';

/**
 * Build a fresh catalog instance. Called once per `IpsoDomain` render
 * (and once per framework test). The returned `DomainCatalog` is a
 * plain JS builder, NOT a hook — each `playbook`/`example` call
 * registers the entry under its label and returns a `DomainRef`.
 *
 * Labels are the reference keys: mentions render them verbatim and
 * validation counts them in the rendered text, so registration rejects
 * a label that equals or contains/is contained by an existing one —
 * overlapping labels would make mention counting ambiguous.
 *
 * All mutation is closure-local: the React Compiler's purity rules
 * apply only at the call sites (which receive the `DomainRef` and use
 * it in JSX without mutation). `Ref`/`Def` are pure renderers.
 *
 * Validation is post-render text scanning (`catalog.validate(out)`),
 * test-only — production never validates.
 */
export function createDomainCatalog(): DomainCatalog {
  const entries = new Map<string, DomainEntry>();
  const order: string[] = [];

  function register(kind: DomainKind, label: string, body: ReactNode, config: DomainConfig = {}): DomainRef {
    const clash = [...entries.keys()].find((existing) => existing.includes(label) || label.includes(existing));
    if (clash !== undefined) {
      throw new Error(
        `DomainCatalog: label "${label}" collides with registered label "${clash}" — ` +
          'labels are reference keys and must be unique and substring-free.',
      );
    }
    const entry: DomainEntry = { kind, label, body, config };
    entries.set(label, entry);
    order.push(label);
    return makeRef(entry);
  }

  return {
    playbook: (label, body, config) => register('playbook', label, body, config),
    example: (label, body, config) => register('example', label, body, config),
    entries,
    order,
    validate: (rendered: string) => {
      validateRendered({ entries, order }, rendered);
    },
  };
}
